import * as React from "react"
import { router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"

export type SubsequenceRule = {
  readonly id: number
  readonly intent_label: string
  readonly smartlead_subsequence_id: number
  readonly delay_days: number
  readonly enabled: boolean
  readonly notes: string
}

export function SubsequenceRulesEditor({
  clientId,
  rules,
  labels,
}: {
  readonly clientId: number
  readonly rules: readonly SubsequenceRule[]
  readonly labels: readonly string[]
}) {
  const [newRule, setNewRule] = React.useState({
    intent_label: labels[0] ?? "",
    smartlead_subsequence_id: "",
    delay_days: "0",
    enabled: true,
    notes: "",
  })

  const createRule = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    router.post(`/settings/clients/${clientId}/subsequence_rules`, { subsequence_rule: newRule }, { preserveScroll: true })
  }

  return (
    <section className="rounded-lg border border-hairline bg-page p-6">
      <h2>Subsequence routing</h2>
      <div className="mt-5 space-y-4">
        {rules.map((rule) => (
          <SubsequenceRuleRow key={rule.id} clientId={clientId} rule={rule} labels={labels} />
        ))}
        {rules.length === 0 && (
          <div className="rounded-md border border-dashed border-hairline p-4">
            <p>No subsequence routing rules yet.</p>
          </div>
        )}
      </div>

      <form onSubmit={createRule} className="mt-6 grid gap-4 lg:grid-cols-[1fr_180px_120px_auto]">
        <label className="space-y-2">
          Intent
          <select
            className="form-control form-control-select"
            value={newRule.intent_label}
            onChange={(event) => setNewRule({ ...newRule, intent_label: event.target.value })}
          >
            {labels.map((label) => (
              <option key={label} value={label}>{label}</option>
            ))}
          </select>
        </label>
        <label className="space-y-2">
          Subsequence ID
          <Input
            type="number"
            min="1"
            value={newRule.smartlead_subsequence_id}
            onChange={(event) => setNewRule({ ...newRule, smartlead_subsequence_id: event.target.value })}
          />
        </label>
        <label className="space-y-2">
          Delay days
          <Input
            type="number"
            min="0"
            value={newRule.delay_days}
            onChange={(event) => setNewRule({ ...newRule, delay_days: event.target.value })}
          />
        </label>
        <div className="flex items-end">
          <Button type="submit">Add rule</Button>
        </div>
      </form>
    </section>
  )
}

function SubsequenceRuleRow({
  clientId,
  rule,
  labels,
}: {
  readonly clientId: number
  readonly rule: SubsequenceRule
  readonly labels: readonly string[]
}) {
  const [data, setData] = React.useState({
    intent_label: rule.intent_label,
    smartlead_subsequence_id: String(rule.smartlead_subsequence_id),
    delay_days: String(rule.delay_days),
    enabled: rule.enabled,
    notes: rule.notes,
  })

  const updateRule = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    router.patch(`/settings/clients/${clientId}/subsequence_rules/${rule.id}`, { subsequence_rule: data }, { preserveScroll: true })
  }

  return (
    <form onSubmit={updateRule} className="grid gap-3 rounded-md border border-hairline bg-surface p-4 lg:grid-cols-[1fr_170px_110px_auto_auto]">
      <label className="space-y-2">
        Intent
        <select
          className="form-control form-control-select"
          value={data.intent_label}
          onChange={(event) => setData({ ...data, intent_label: event.target.value })}
        >
          {labels.map((label) => (
            <option key={label} value={label}>{label}</option>
          ))}
        </select>
      </label>
      <label className="space-y-2">
        Subsequence ID
        <Input
          type="number"
          min="1"
          value={data.smartlead_subsequence_id}
          onChange={(event) => setData({ ...data, smartlead_subsequence_id: event.target.value })}
        />
      </label>
      <label className="space-y-2">
        Delay
        <Input
          type="number"
          min="0"
          value={data.delay_days}
          onChange={(event) => setData({ ...data, delay_days: event.target.value })}
        />
      </label>
      <label className="flex items-center gap-2 font-normal text-ink-body">
        <Checkbox
          checked={data.enabled}
          onChange={(event) => setData({ ...data, enabled: event.target.checked })}
        />
        <span>Enabled</span>
      </label>
      <div className="flex items-end gap-2">
        <Button type="submit" variant="secondary">Save</Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => router.delete(`/settings/clients/${clientId}/subsequence_rules/${rule.id}`, { preserveScroll: true })}
        >
          Delete
        </Button>
      </div>
    </form>
  )
}
